import React, { useEffect, useMemo, useState } from 'react';
import { CreditCard, Edit2, Loader2, Plus, Settings2, Trash2, UserCog, Zap } from 'lucide-react';
import { requestsService } from '../../services/requestsService';
import UniversalField from '../shared/forms/UniversalField';

const DEFAULT_BUDGET = { credits: 0, dailyLimit: 50, weeklyLimit: 200, spentToday: 0, spentWeek: 0 };
const createDefaultAgentForm = () => ({
  name: '',
  description: '',
  categories: [],
  autoEngage: false,
  dailyLimit: 50,
  weeklyLimit: 200,
  status: 'active',
  sentToday: 0,
  sentWeek: 0,
  permissions: {
    canSendOffers: true,
    canAutoSendOffers: false,
    canUseDiscounts: false,
    requiresManualApproval: true
  },
  limits: {
    offersPerDay: 10,
    offersPerWeek: 50,
    creditsPerDay: 50,
    creditsPerWeek: 200
  },
  pricingPolicy: {
    basePrice: '',
    minPrice: '',
    allowDiscounts: false,
    maxDiscountPercent: 0,
    maxDiscountAmount: 0
  },
  matchingPolicy: {
    minBudget: '',
    maxBudget: '',
    preferredLocations: '',
    allowCrossCategory: true
  }
});
const DEFAULT_PLAY_FORM = { name: '', category: '', valueProp: '', price: '', timeframe: '', description: '', autoRespond: false };

const ProviderView = ({ bookId, user, isDarkMode, bookData }) => {
  const providerConfig = {
    fields: bookData?.providerFields || [],
    categories: bookData?.providerCategories || ['General Services'],
    settings: bookData?.providerSettings || {}
  };

  const creditsConfig = bookData?.creditsSettings || {
    costPerOffer: 5,
    costPerResponse: 1,
    freeCredits: 50
  };

  const providerId = user?.uid || null;
  const requiresApproval = !!providerConfig.settings.requireApproval;

  const buildProviderForm = () => {
    const initial = {};
    providerConfig.fields.forEach((field) => {
      if (field.name !== 'category') initial[field.name] = '';
    });
    return initial;
  };

  const mapProviderToForm = (profile) => {
    const initial = buildProviderForm();
    providerConfig.fields.forEach((field) => {
      if (field.name !== 'category') initial[field.name] = profile?.[field.name] || '';
    });
    return initial;
  };

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [providerProfile, setProviderProfile] = useState(null);
  const [providerForm, setProviderForm] = useState(buildProviderForm);
  const [providerErrors, setProviderErrors] = useState({});
  const [editingBusiness, setEditingBusiness] = useState(false);
  const [savingBusiness, setSavingBusiness] = useState(false);
  const [budget, setBudget] = useState({ ...DEFAULT_BUDGET, credits: creditsConfig.freeCredits });

  const [agents, setAgents] = useState([]);
  const [selectedAgentId, setSelectedAgentId] = useState(null);
  const [agentForm, setAgentForm] = useState(createDefaultAgentForm);
  const [agentErrors, setAgentErrors] = useState({});
  const [editingAgent, setEditingAgent] = useState(null);
  const [savingAgent, setSavingAgent] = useState(false);
  const [showAgentModal, setShowAgentModal] = useState(false);

  const [plays, setPlays] = useState([]);
  const [playForm, setPlayForm] = useState(DEFAULT_PLAY_FORM);
  const [playErrors, setPlayErrors] = useState({});
  const [editingPlay, setEditingPlay] = useState(null);
  const [savingPlay, setSavingPlay] = useState(false);
  const [showPlayModal, setShowPlayModal] = useState(false);

  const [requests, setRequests] = useState([]);
  const [offers, setOffers] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [selectedPlay, setSelectedPlay] = useState(null);
  const [offerForm, setOfferForm] = useState({ valueProp: '', price: '', timeframe: '' });
  const [sendingOffer, setSendingOffer] = useState(false);
  const [showOfferModal, setShowOfferModal] = useState(false);

  const [activeTab, setActiveTab] = useState('agents');

  const selectedAgent = useMemo(
    () => agents.find((agent) => agent.id === selectedAgentId) || null,
    [agents, selectedAgentId]
  );

  const providerVerified = !!providerProfile && (!requiresApproval || providerProfile.verified === true);
  const selectedAgentPlays = useMemo(
    () => plays.filter((play) => play.agentId === selectedAgentId),
    [plays, selectedAgentId]
  );
  const selectedAgentOffers = useMemo(
    () => offers.filter((offer) => offer.agentId === selectedAgentId),
    [offers, selectedAgentId]
  );

  const loadWorkspace = async () => {
    if (!bookId || !providerId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');

    try {
      let profile = await requestsService.getProviderProfileByUserId(bookId, providerId);
      if (profile && profile.id !== providerId) {
        profile = await requestsService.migrateProviderProfileToCanonicalId(bookId, providerId, profile);
      }

      if (!profile) {
        setProviderProfile(null);
        setProviderForm(buildProviderForm());
        setAgents([]);
        setPlays([]);
        setOffers([]);
        setRequests([]);
        setSelectedAgentId(null);
        setBudget({ ...DEFAULT_BUDGET, credits: creditsConfig.freeCredits });
        setLoading(false);
        return;
      }

      const [creditDoc, agentDocs, requestDocs, offerDocs, playDocs] = await Promise.all([
        requestsService.getProviderCredits(bookId, providerId),
        requestsService.getProviderAgents(bookId, providerId),
        requestsService.getOpenRequests(bookId),
        requestsService.getOffersByProvider(bookId, providerId),
        requestsService.getProviderPresets(bookId, providerId)
      ]);

      if (!creditDoc) {
        await requestsService.initializeProviderCredits(bookId, providerId, {
          credits: creditsConfig.freeCredits,
          dailyLimit: 50,
          weeklyLimit: 200,
          spentToday: 0,
          spentWeek: 0
        });
      }

      setProviderProfile(profile);
      setProviderForm(mapProviderToForm(profile));
      setBudget({
        credits: creditDoc?.credits ?? creditsConfig.freeCredits,
        dailyLimit: creditDoc?.dailyLimit ?? 50,
        weeklyLimit: creditDoc?.weeklyLimit ?? 200,
        spentToday: creditDoc?.spentToday ?? 0,
        spentWeek: creditDoc?.spentWeek ?? 0
      });
      setAgents(agentDocs);
      setRequests(requestDocs);
      setOffers(offerDocs);
      setPlays(playDocs);

      if (agentDocs.length > 0) {
        const defaultAgent = agentDocs.find((agent) => agent.isDefault) || agentDocs[0];
        setSelectedAgentId((current) => current && agentDocs.some((agent) => agent.id === current) ? current : defaultAgent.id);
      }
    } catch (loadError) {
      console.error('Failed to load provider workspace:', loadError);
      setError('Failed to load Agent King provider workspace.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setProviderForm(buildProviderForm());
  }, [bookId]);

  useEffect(() => {
    loadWorkspace();
  }, [bookId, providerId]);

  const updateProviderField = (fieldName, value) => {
    setProviderForm((prev) => ({ ...prev, [fieldName]: value }));
    if (providerErrors[fieldName]) setProviderErrors((prev) => ({ ...prev, [fieldName]: null }));
  };

  const validateProviderForm = () => {
    const errors = {};
    providerConfig.fields.forEach((field) => {
      if (field.required && field.name !== 'category' && !providerForm[field.name]) {
        errors[field.name] = `${field.label} is required`;
      }
    });
    return errors;
  };

  const validateAgentForm = () => {
    const errors = {};
    if (!agentForm.name.trim()) errors.name = 'Agent name is required';
    if (!agentForm.categories.length) errors.categories = 'Select at least one category';
    if ((agentForm.limits?.offersPerDay ?? 0) < 0) errors.offersPerDay = 'Offers per day cannot be negative';
    if ((agentForm.limits?.offersPerWeek ?? 0) < 0) errors.offersPerWeek = 'Offers per week cannot be negative';
    if ((agentForm.pricingPolicy?.minPrice ?? 0) < 0) errors.minPrice = 'Minimum price cannot be negative';
    if ((agentForm.pricingPolicy?.maxDiscountPercent ?? 0) < 0) errors.maxDiscountPercent = 'Discount percent cannot be negative';
    if ((agentForm.pricingPolicy?.maxDiscountAmount ?? 0) < 0) errors.maxDiscountAmount = 'Discount amount cannot be negative';
    return errors;
  };

  const validatePlayForm = () => {
    const errors = {};
    if (!selectedAgent) errors.agent = 'Select an agent first';
    if (!playForm.name.trim()) errors.name = 'Play name is required';
    if (!playForm.category) errors.category = 'Select a category';
    if (!playForm.valueProp.trim()) errors.valueProp = 'Agent message is required';
    return errors;
  };

  const normalizeAgentForm = (agent = {}) => {
    const defaults = createDefaultAgentForm();
    return {
      ...defaults,
      ...agent,
      categories: agent.categories || defaults.categories,
      permissions: {
        ...defaults.permissions,
        ...(agent.permissions || {})
      },
      limits: {
        ...defaults.limits,
        ...(agent.limits || {})
      },
      pricingPolicy: {
        ...defaults.pricingPolicy,
        ...(agent.pricingPolicy || {})
      },
      matchingPolicy: {
        ...defaults.matchingPolicy,
        ...(agent.matchingPolicy || {})
      }
    };
  };

  const resetAgentEditor = (agent = null) => {
    setEditingAgent(agent);
    setAgentErrors({});
    setAgentForm(normalizeAgentForm(agent || {}));
  };

  const resetPlayEditor = (play = null) => {
    setEditingPlay(play);
    setPlayErrors({});
    setPlayForm(play ? {
      name: play.name || '',
      category: play.category || '',
      valueProp: play.valueProp || '',
      price: play.price || '',
      timeframe: play.timeframe || '',
      description: play.description || '',
      autoRespond: !!play.autoRespond
    } : { ...DEFAULT_PLAY_FORM, category: selectedAgent?.categories?.[0] || '' });
  };

  const setAgentNestedField = (section, field, value) => {
    setAgentForm((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
    if (agentErrors[field]) {
      setAgentErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const handleInitialSetup = async (e) => {
    e.preventDefault();

    const nextProviderErrors = validateProviderForm();
    const nextAgentErrors = validateAgentForm();
    setProviderErrors(nextProviderErrors);
    setAgentErrors(nextAgentErrors);
    if (Object.keys(nextProviderErrors).length || Object.keys(nextAgentErrors).length) return;

    setSavingBusiness(true);
    setError('');

    try {
      await requestsService.createProviderProfile(bookId, providerId, {
        ...providerForm,
        verified: !requiresApproval,
        featured: false,
        status: requiresApproval ? 'pending_approval' : 'active'
      });

      await requestsService.initializeProviderCredits(bookId, providerId, {
        credits: creditsConfig.freeCredits,
        dailyLimit: 50,
        weeklyLimit: 200,
        spentToday: 0,
        spentWeek: 0
      });

      await requestsService.createProviderAgent(bookId, providerId, {
        ...agentForm,
        isDefault: true
      });

      await loadWorkspace();
    } catch (setupError) {
      console.error('Failed to complete provider setup:', setupError);
      setError('Failed to activate the provider workspace.');
    } finally {
      setSavingBusiness(false);
    }
  };

  const saveBusinessProfile = async () => {
    const nextProviderErrors = validateProviderForm();
    setProviderErrors(nextProviderErrors);
    if (Object.keys(nextProviderErrors).length) return;

    setSavingBusiness(true);
    setError('');

    try {
      await requestsService.updateProviderProfile(bookId, providerId, providerForm);
      setProviderProfile((prev) => ({ ...prev, ...providerForm }));
      setEditingBusiness(false);
    } catch (saveError) {
      console.error('Failed to save provider profile:', saveError);
      setError('Failed to save business profile.');
    } finally {
      setSavingBusiness(false);
    }
  };

  const saveAgent = async () => {
    const nextAgentErrors = validateAgentForm();
    setAgentErrors(nextAgentErrors);
    if (Object.keys(nextAgentErrors).length) return;

    setSavingAgent(true);
    setError('');

    try {
      if (editingAgent) {
        await requestsService.updateProviderAgent(bookId, editingAgent.id, {
          ...agentForm,
          providerId
        });
      } else {
        await requestsService.createProviderAgent(bookId, providerId, {
          ...agentForm,
          isDefault: agents.length === 0
        });
      }

      await loadWorkspace();
      setShowAgentModal(false);
    } catch (saveError) {
      console.error('Failed to save agent:', saveError);
      setError('Failed to save agent.');
    } finally {
      setSavingAgent(false);
    }
  };

  const deleteAgent = async (agent) => {
    if (!window.confirm(`Delete agent "${agent.name}"?`)) return;

    try {
      await requestsService.deleteProviderAgent(bookId, agent.id);
      await loadWorkspace();
    } catch (deleteError) {
      console.error('Failed to delete agent:', deleteError);
      setError('Failed to delete agent.');
    }
  };

  const savePlay = async () => {
    const nextPlayErrors = validatePlayForm();
    setPlayErrors(nextPlayErrors);
    if (Object.keys(nextPlayErrors).length) return;

    setSavingPlay(true);
    setError('');

    try {
      const payload = {
        ...playForm,
        providerId,
        agentId: selectedAgent.id,
        agentName: selectedAgent.name
      };

      if (editingPlay) {
        await requestsService.updatePresetOffer(bookId, editingPlay.id, payload);
      } else {
        await requestsService.createPresetOffer(bookId, providerId, payload);
      }

      await loadWorkspace();
      setShowPlayModal(false);
    } catch (saveError) {
      console.error('Failed to save play:', saveError);
      setError('Failed to save agent play.');
    } finally {
      setSavingPlay(false);
    }
  };

  const deletePlay = async (play) => {
    if (!window.confirm(`Delete play "${play.name}"?`)) return;

    try {
      await requestsService.deletePresetOffer(bookId, play.id);
      await loadWorkspace();
    } catch (deleteError) {
      console.error('Failed to delete play:', deleteError);
      setError('Failed to delete agent play.');
    }
  };

  const togglePlayAutoRespond = async (play) => {
    try {
      await requestsService.updatePresetOffer(bookId, play.id, {
        autoRespond: !play.autoRespond
      });
      await loadWorkspace();
    } catch (toggleError) {
      console.error('Failed to update play automation:', toggleError);
      setError('Failed to update play automation.');
    }
  };

  const openOfferComposer = (request, play = null) => {
    if (!selectedAgent) {
      setError('Select an agent before responding.');
      return;
    }
    if (selectedAgent.permissions?.canSendOffers === false) {
      setError('This agent is not permitted to send offers.');
      return;
    }
    if (!providerVerified) {
      setError('This provider must be verified before agents can send responses.');
      return;
    }
    if (selectedAgent.matchingPolicy?.allowCrossCategory === false && !selectedAgent.categories.includes(request.category)) {
      setError('This agent is restricted to its own categories.');
      return;
    }
    const requestBudget = Number(request.budget || 0);
    if (selectedAgent.matchingPolicy?.minBudget && requestBudget && requestBudget < Number(selectedAgent.matchingPolicy.minBudget)) {
      setError('This mission is below the agent minimum budget.');
      return;
    }
    if (selectedAgent.matchingPolicy?.maxBudget && requestBudget && requestBudget > Number(selectedAgent.matchingPolicy.maxBudget)) {
      setError('This mission exceeds the agent maximum budget.');
      return;
    }
    if (selectedAgent.matchingPolicy?.preferredLocations && request.location) {
      const allowedLocations = selectedAgent.matchingPolicy.preferredLocations
        .split(',')
        .map((item) => item.trim().toLowerCase())
        .filter(Boolean);
      if (allowedLocations.length > 0 && !allowedLocations.some((item) => request.location.toLowerCase().includes(item))) {
        setError('This mission is outside the agent preferred locations.');
        return;
      }
    }
    if ((selectedAgent.sentToday || 0) >= (selectedAgent.limits?.offersPerDay ?? Infinity)) {
      setError('This agent has reached its daily offer limit.');
      return;
    }
    if ((selectedAgent.sentWeek || 0) >= (selectedAgent.limits?.offersPerWeek ?? Infinity)) {
      setError('This agent has reached its weekly offer limit.');
      return;
    }
    if (budget.credits < creditsConfig.costPerOffer) {
      setError('Insufficient credits to send a response.');
      return;
    }
    if ((selectedAgent.spentToday || 0) + creditsConfig.costPerOffer > (selectedAgent.limits?.creditsPerDay ?? selectedAgent.dailyLimit)) {
      setError('This agent has reached its daily limit.');
      return;
    }
    if ((selectedAgent.spentWeek || 0) + creditsConfig.costPerOffer > (selectedAgent.limits?.creditsPerWeek ?? selectedAgent.weeklyLimit)) {
      setError('This agent has reached its weekly limit.');
      return;
    }

    setSelectedRequest(request);
    setSelectedPlay(play);
    setOfferForm(play ? {
      valueProp: play.valueProp,
      price: play.price,
      timeframe: play.timeframe
    } : {
      valueProp: `Agent ${selectedAgent.name} can help deliver "${request.title || request.description}".`,
      price: '',
      timeframe: ''
    });
    setShowOfferModal(true);
  };

  const sendOffer = async () => {
    if (!selectedAgent || !selectedRequest) return;

    setSendingOffer(true);
    setError('');

    try {
      const numericPrice = Number(offerForm.price || 0);
      const basePrice = Number(selectedAgent.pricingPolicy?.basePrice || 0);
      const minPrice = Number(selectedAgent.pricingPolicy?.minPrice || 0);
      const maxDiscountPercent = Number(selectedAgent.pricingPolicy?.maxDiscountPercent || 0);
      const maxDiscountAmount = Number(selectedAgent.pricingPolicy?.maxDiscountAmount || 0);

      if (minPrice && numericPrice && numericPrice < minPrice) {
        setError(`Offer price is below the agent minimum price of ${minPrice}.`);
        setSendingOffer(false);
        return;
      }
      if (selectedAgent.pricingPolicy?.allowDiscounts === false && basePrice && numericPrice && numericPrice < basePrice) {
        setError('Discounts are disabled for this agent.');
        setSendingOffer(false);
        return;
      }
      if (selectedAgent.permissions?.canUseDiscounts === false && basePrice && numericPrice && numericPrice < basePrice) {
        setError('This agent is not allowed to apply discounts.');
        setSendingOffer(false);
        return;
      }
      if (basePrice && numericPrice && numericPrice < basePrice) {
        const discountAmount = basePrice - numericPrice;
        const discountPercent = (discountAmount / basePrice) * 100;
        if (maxDiscountAmount && discountAmount > maxDiscountAmount) {
          setError(`Discount exceeds the agent limit of ${maxDiscountAmount}.`);
          setSendingOffer(false);
          return;
        }
        if (maxDiscountPercent && discountPercent > maxDiscountPercent) {
          setError(`Discount exceeds the agent limit of ${maxDiscountPercent}%.`);
          setSendingOffer(false);
          return;
        }
      }

      await requestsService.sendAgentOffer(
        bookId,
        selectedRequest.id,
        selectedAgent.id,
        {
          valueProp: offerForm.valueProp,
          price: offerForm.price,
          timeframe: offerForm.timeframe
        },
        selectedPlay?.id || null
      );

      setShowOfferModal(false);
      setSelectedPlay(null);
      await loadWorkspace();
      setActiveTab('activity');
    } catch (sendError) {
      console.error('Failed to send agent response:', sendError);
      setError(sendError?.message || sendError?.details || 'Failed to send agent response.');
    } finally {
      setSendingOffer(false);
    }
  };

  if (!user) {
    return (
      <div className={`min-h-[60vh] flex items-center justify-center px-4 ${isDarkMode ? 'dark' : ''}`}>
        <div className="max-w-lg w-full rounded-2xl border border-slate-200 bg-white p-8 text-center dark:border-slate-700 dark:bg-slate-800">
          <h2 className="mb-3 text-2xl font-bold text-slate-900 dark:text-white">Sign in to configure Agent King</h2>
          <p className="text-slate-600 dark:text-slate-300">
            Business profiles, provider agents, plays, and responses are owned by the authenticated provider account.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <div className="flex items-center gap-3 text-slate-600 dark:text-slate-300">
          <Loader2 className="h-5 w-5 animate-spin" />
          Loading Agent King workspace...
        </div>
      </div>
    );
  }

  if (!providerProfile) {
    return (
      <div className={`min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 py-12 px-4 ${isDarkMode ? 'dark bg-[#0f172a]' : 'bg-slate-50'}`}>
        <div className="mx-auto grid max-w-6xl gap-10 lg:grid-cols-2">
          <div>
            <h1 className="mb-4 text-4xl font-extrabold text-slate-900 dark:text-white">Set Up The Provider Workspace</h1>
            <p className="mb-6 text-lg text-slate-600 dark:text-slate-300">
              Agent King separates the business identity from the individual agents that represent it in the market.
            </p>
            <div className="space-y-3 text-slate-700 dark:text-slate-300">
              <p>Business profile handles identity, verification, and ownership.</p>
              <p>Agents handle categories, plays, limits, and response strategy.</p>
              <p>{creditsConfig.freeCredits} provider credits are included when the workspace is activated.</p>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-xl dark:border-slate-700 dark:bg-slate-800">
            <h2 className="mb-6 text-2xl font-bold text-slate-900 dark:text-white">Initial Setup</h2>
            {error && <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-200">{error}</div>}

            <form onSubmit={handleInitialSetup} className="space-y-8">
              <section className="space-y-4">
                <h3 className="text-sm font-bold uppercase tracking-wide text-slate-900 dark:text-white">Business Profile</h3>
                {providerConfig.fields
                  .filter((field) => field.name !== 'category')
                  .sort((a, b) => a.order - b.order)
                  .map((field) => (
                    <UniversalField
                      key={field.id}
                      field={field}
                      value={providerForm[field.name]}
                      onChange={(value) => updateProviderField(field.name, value)}
                      error={providerErrors[field.name]}
                    />
                  ))}
              </section>

              <section className="space-y-4">
                <h3 className="text-sm font-bold uppercase tracking-wide text-slate-900 dark:text-white">First Agent</h3>
                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">Agent Name</label>
                  <input
                    value={agentForm.name}
                    onChange={(e) => setAgentForm((prev) => ({ ...prev, name: e.target.value }))}
                    className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900 dark:border-slate-600 dark:bg-slate-900 dark:text-white"
                    placeholder="e.g. Premium Leads Agent"
                  />
                  {agentErrors.name && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{agentErrors.name}</p>}
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">Agent Role</label>
                  <textarea
                    value={agentForm.description}
                    onChange={(e) => setAgentForm((prev) => ({ ...prev, description: e.target.value }))}
                    rows={3}
                    className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900 dark:border-slate-600 dark:bg-slate-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">Categories</label>
                  <div className="grid grid-cols-2 gap-2">
                    {providerConfig.categories.map((category) => {
                      const selected = agentForm.categories.includes(category);
                      return (
                        <button
                          key={category}
                          type="button"
                          onClick={() => setAgentForm((prev) => ({
                            ...prev,
                            categories: selected
                              ? prev.categories.filter((item) => item !== category)
                              : prev.categories.length >= 4
                                ? prev.categories
                                : [...prev.categories, category]
                          }))}
                          className={`rounded-lg border px-3 py-2 text-left text-sm ${
                            selected
                              ? 'border-emerald-500 bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300'
                              : 'border-slate-300 bg-white text-slate-700 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-300'
                          }`}
                        >
                          {category}
                        </button>
                      );
                    })}
                  </div>
                  {agentErrors.categories && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{agentErrors.categories}</p>}
                </div>
              </section>

              <button
                type="submit"
                disabled={savingBusiness}
                className="w-full rounded-xl bg-emerald-600 px-4 py-3 font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
              >
                {savingBusiness ? 'Activating...' : 'Create Workspace'}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <div className="flex flex-col gap-4 border-b border-slate-200 pb-6 dark:border-slate-700 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white">{providerProfile.businessName || 'Provider Workspace'}</h1>
          <p className="text-slate-500 dark:text-slate-400">
            Business profile owns the account. Agents own execution.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {['agents', 'missions', 'plays', 'activity', 'business'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`rounded-lg px-4 py-2 text-sm font-semibold capitalize ${
                activeTab === tab
                  ? 'bg-slate-900 text-white dark:bg-white dark:text-slate-900'
                  : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>

      {error && <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-200">{error}</div>}
      {!providerVerified && <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200">Provider approval is pending. Agents can be configured now but cannot send live responses yet.</div>}

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-4">
        <div className="space-y-4 xl:col-span-1">
          <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-700 dark:bg-slate-800">
            <div className="mb-4 flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-emerald-500" />
              <h3 className="font-semibold text-slate-900 dark:text-white">Provider Wallet</h3>
            </div>
            <div className="text-3xl font-bold text-emerald-600 dark:text-emerald-400">{budget.credits}</div>
            <div className="mt-3 space-y-2 text-sm text-slate-500 dark:text-slate-400">
              <div className="flex justify-between"><span>Per response</span><span className="font-medium text-slate-900 dark:text-white">{creditsConfig.costPerOffer}</span></div>
              <div className="flex justify-between"><span>Daily limit</span><span className="font-medium text-slate-900 dark:text-white">{budget.dailyLimit}</span></div>
              <div className="flex justify-between"><span>Weekly limit</span><span className="font-medium text-slate-900 dark:text-white">{budget.weeklyLimit}</span></div>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-700 dark:bg-slate-800">
            <div className="mb-4 flex items-center gap-2">
              <UserCog className="h-5 w-5 text-sky-500" />
              <h3 className="font-semibold text-slate-900 dark:text-white">Selected Agent</h3>
            </div>
            {selectedAgent ? (
              <div className="space-y-3 text-sm">
                <div className="text-lg font-semibold text-slate-900 dark:text-white">{selectedAgent.name}</div>
                <p className="text-slate-500 dark:text-slate-400">{selectedAgent.description || 'No role description yet.'}</p>
                <div className="flex flex-wrap gap-2">
                  {selectedAgent.categories.map((category) => (
                    <span key={category} className="rounded-full bg-sky-50 px-3 py-1 text-xs font-medium text-sky-700 dark:bg-sky-900/20 dark:text-sky-300">{category}</span>
                  ))}
                </div>
                <div className="space-y-1 text-slate-500 dark:text-slate-400">
                  <div>Offers: <span className="font-medium text-slate-900 dark:text-white">{selectedAgent.sentToday || 0}/{selectedAgent.limits?.offersPerDay ?? '-'}</span> today</div>
                  <div>Credits: <span className="font-medium text-slate-900 dark:text-white">{selectedAgent.spentToday || 0}/{selectedAgent.limits?.creditsPerDay ?? '-'}</span> today</div>
                  <div>Discounts: <span className="font-medium text-slate-900 dark:text-white">{selectedAgent.permissions?.canUseDiscounts ? `Up to ${selectedAgent.pricingPolicy?.maxDiscountPercent || 0}%` : 'Disabled'}</span></div>
                </div>
              </div>
            ) : (
              <p className="text-sm text-slate-500 dark:text-slate-400">Create and select an agent to continue.</p>
            )}
          </div>
        </div>
        <div className="xl:col-span-3">
          {activeTab === 'agents' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-slate-900 dark:text-white">Provider Agents</h2>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Each agent can target different categories and run different plays.</p>
                </div>
                <button
                  onClick={() => {
                    resetAgentEditor();
                    setShowAgentModal(true);
                  }}
                  className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-white hover:bg-emerald-700"
                >
                  <Plus className="h-4 w-4" />
                  New Agent
                </button>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                {agents.map((agent) => (
                  <div key={agent.id} className={`rounded-2xl border p-5 ${selectedAgentId === agent.id ? 'border-emerald-500 bg-emerald-50/60 dark:bg-emerald-900/10' : 'border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800'}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h3 className="font-semibold text-slate-900 dark:text-white">{agent.name}</h3>
                        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{agent.description || 'No description provided.'}</p>
                      </div>
                      <div className="flex gap-1">
                        <button
                          onClick={() => {
                            resetAgentEditor(agent);
                            setShowAgentModal(true);
                          }}
                          className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-700"
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => deleteAgent(agent)}
                          className="rounded-lg p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>

                    <div className="mt-4 flex flex-wrap gap-2">
                      {agent.categories.map((category) => (
                        <span key={category} className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700 dark:bg-slate-700 dark:text-slate-200">{category}</span>
                      ))}
                    </div>

                    <div className="mt-4 grid grid-cols-2 gap-3 text-sm text-slate-500 dark:text-slate-400">
                      <div>Daily: <span className="font-medium text-slate-900 dark:text-white">{agent.spentToday || 0}/{agent.dailyLimit}</span></div>
                      <div>Weekly: <span className="font-medium text-slate-900 dark:text-white">{agent.spentWeek || 0}/{agent.weeklyLimit}</span></div>
                      <div>Offers/day: <span className="font-medium text-slate-900 dark:text-white">{agent.sentToday || 0}/{agent.limits?.offersPerDay ?? '-'}</span></div>
                      <div>Discounts: <span className="font-medium text-slate-900 dark:text-white">{agent.permissions?.canUseDiscounts ? 'Enabled' : 'Disabled'}</span></div>
                    </div>

                    <button
                      onClick={() => setSelectedAgentId(agent.id)}
                      className="mt-5 w-full rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-700"
                    >
                      {selectedAgentId === agent.id ? 'Selected' : 'Use This Agent'}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'missions' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-bold text-slate-900 dark:text-white">Open Outcome Missions</h2>
                <p className="text-sm text-slate-500 dark:text-slate-400">{selectedAgent ? `Responses will be attributed to ${selectedAgent.name}.` : 'Select an agent first.'}</p>
              </div>

              {!selectedAgent && <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-6 py-12 text-center text-slate-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400">Select an agent to review missions.</div>}

              {selectedAgent && requests.length === 0 && <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-6 py-12 text-center text-slate-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400">No missions are available right now.</div>}

              {selectedAgent && requests.map((request) => {
                const matchingPlays = selectedAgentPlays.filter((play) => play.category === request.category && play.active !== false);
                const categoryMatch = selectedAgent.categories.includes(request.category);

                return (
                  <div key={request.id} className="rounded-2xl border border-slate-200 bg-white p-6 dark:border-slate-700 dark:bg-slate-800">
                    <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                      <div>
                        <div className="mb-2 flex items-center gap-2">
                          <h3 className="text-lg font-semibold text-slate-900 dark:text-white">{request.title || request.description}</h3>
                          <span className={`rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide ${categoryMatch ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300' : 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200'}`}>{categoryMatch ? 'Matched' : 'Cross-category'}</span>
                        </div>
                        <p className="text-slate-600 dark:text-slate-300">{request.description}</p>
                        <div className="mt-3 flex flex-wrap gap-3 text-sm text-slate-500 dark:text-slate-400">
                          <span>{request.category}</span>
                          {request.budget && <span>Budget: R{request.budget}</span>}
                          {request.timeframe && <span>Timeframe: {request.timeframe}</span>}
                        </div>
                      </div>
                      <button
                        onClick={() => openOfferComposer(request)}
                        disabled={!providerVerified}
                        className="rounded-lg bg-emerald-600 px-4 py-2 text-white hover:bg-emerald-700 disabled:opacity-50"
                      >
                        Custom Response
                      </button>
                    </div>

                    {matchingPlays.length > 0 && (
                      <div className="mt-5 rounded-xl border border-purple-200 bg-purple-50 p-4 dark:border-purple-800 dark:bg-purple-900/10">
                        <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-purple-900 dark:text-purple-200">
                          <Zap className="h-4 w-4" />
                          Recommended Plays
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {matchingPlays.map((play) => (
                            <button
                              key={play.id}
                              onClick={() => openOfferComposer(request, play)}
                              className="rounded-lg border border-purple-300 bg-white px-3 py-2 text-left text-sm text-slate-900 hover:bg-purple-50 dark:border-purple-700 dark:bg-slate-800 dark:text-white"
                            >
                              <div className="font-medium">{play.name}</div>
                              <div className="text-xs text-slate-500 dark:text-slate-400">R{play.price || '-'} | {play.timeframe || 'Flexible'}</div>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {activeTab === 'plays' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-slate-900 dark:text-white">Agent Plays</h2>
                  <p className="text-sm text-slate-500 dark:text-slate-400">{selectedAgent ? `Reusable responses owned by ${selectedAgent.name}.` : 'Select an agent first.'}</p>
                </div>
                <button
                  onClick={() => {
                    resetPlayEditor();
                    setShowPlayModal(true);
                  }}
                  disabled={!selectedAgent}
                  className="inline-flex items-center gap-2 rounded-lg bg-purple-600 px-4 py-2 text-white hover:bg-purple-700 disabled:opacity-50"
                >
                  <Plus className="h-4 w-4" />
                  New Play
                </button>
              </div>

              {!selectedAgent && <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-6 py-12 text-center text-slate-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400">Select an agent to manage plays.</div>}

              {selectedAgent && selectedAgentPlays.length === 0 && <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-6 py-12 text-center text-slate-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400">No plays exist for this agent yet.</div>}

              <div className="grid gap-4 md:grid-cols-2">
                {selectedAgentPlays.map((play) => (
                  <div key={play.id} className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-700 dark:bg-slate-800">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h3 className="font-semibold text-slate-900 dark:text-white">{play.name}</h3>
                        <p className="text-sm text-slate-500 dark:text-slate-400">{play.category}</p>
                      </div>
                      <div className="flex gap-1">
                        <button
                          onClick={() => {
                            resetPlayEditor(play);
                            setShowPlayModal(true);
                          }}
                          className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-700"
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => deletePlay(play)}
                          className="rounded-lg p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                    <p className="mt-3 text-sm text-slate-600 dark:text-slate-300">{play.valueProp}</p>
                    <div className="mt-4 flex items-center justify-between text-sm text-slate-500 dark:text-slate-400">
                      <span>R{play.price || '-'}</span>
                      <span>{play.timeframe || 'Flexible'}</span>
                    </div>
                    <label className="mt-4 flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
                      <input
                        type="checkbox"
                        checked={!!play.autoRespond}
                        onChange={() => togglePlayAutoRespond(play)}
                      />
                      Auto-engage
                    </label>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'activity' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-bold text-slate-900 dark:text-white">Agent Activity</h2>
                <p className="text-sm text-slate-500 dark:text-slate-400">{selectedAgent ? `Offer history for ${selectedAgent.name}.` : 'Select an agent first.'}</p>
              </div>

              {!selectedAgent && <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-6 py-12 text-center text-slate-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400">Select an agent to inspect activity.</div>}

              {selectedAgent && selectedAgentOffers.length === 0 && <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-6 py-12 text-center text-slate-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400">This agent has not responded to any missions yet.</div>}

              <div className="space-y-4">
                {selectedAgentOffers.map((offer) => (
                  <div key={offer.id} className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-700 dark:bg-slate-800">
                    <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                      <div>
                        <div className="font-semibold text-slate-900 dark:text-white">{offer.agentName || selectedAgent.name}</div>
                        <div className="text-sm text-slate-500 dark:text-slate-400">Mission: {offer.requestId}</div>
                        <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">{offer.valueProp}</p>
                      </div>
                      <div className="text-right text-sm">
                        <div className="font-semibold text-emerald-600 dark:text-emerald-400">R{offer.price || '-'}</div>
                        <div className="text-slate-500 dark:text-slate-400">{offer.timeframe || 'Flexible'}</div>
                        <div className="mt-2 rounded-full bg-amber-100 px-2.5 py-1 text-xs font-bold uppercase tracking-wide text-amber-700 dark:bg-amber-900/20 dark:text-amber-300">{offer.status || 'pending'}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'business' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-slate-900 dark:text-white">Business Profile</h2>
                  <p className="text-sm text-slate-500 dark:text-slate-400">This identity owns all provider agents and permissions.</p>
                </div>
                <button
                  onClick={() => setEditingBusiness((prev) => !prev)}
                  className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-white hover:bg-slate-800 dark:bg-white dark:text-slate-900"
                >
                  <Settings2 className="h-4 w-4" />
                  {editingBusiness ? 'Close Editor' : 'Edit Business'}
                </button>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white p-6 dark:border-slate-700 dark:bg-slate-800">
                <div className="space-y-4">
                  {providerConfig.fields
                    .filter((field) => field.name !== 'category')
                    .sort((a, b) => a.order - b.order)
                    .map((field) => (
                      <UniversalField
                        key={field.id}
                        field={field}
                        value={providerForm[field.name]}
                        onChange={(value) => updateProviderField(field.name, value)}
                        error={providerErrors[field.name]}
                        disabled={!editingBusiness}
                      />
                    ))}
                </div>
                {editingBusiness && (
                  <div className="mt-6 flex justify-end">
                    <button
                      onClick={saveBusinessProfile}
                      disabled={savingBusiness}
                      className="rounded-lg bg-emerald-600 px-4 py-2 text-white hover:bg-emerald-700 disabled:opacity-50"
                    >
                      {savingBusiness ? 'Saving...' : 'Save Business Profile'}
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {showAgentModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setShowAgentModal(false)}
        >
          <div
            className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl dark:bg-slate-800"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="mb-4 text-2xl font-bold text-slate-900 dark:text-white">{editingAgent ? 'Edit Agent' : 'Create Agent'}</h3>
            <div className="space-y-4">
              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">Agent Name</label>
                <input
                  value={agentForm.name}
                  onChange={(e) => setAgentForm((prev) => ({ ...prev, name: e.target.value }))}
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900 dark:border-slate-600 dark:bg-slate-900 dark:text-white"
                />
                {agentErrors.name && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{agentErrors.name}</p>}
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">Role</label>
                <textarea
                  value={agentForm.description}
                  onChange={(e) => setAgentForm((prev) => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900 dark:border-slate-600 dark:bg-slate-900 dark:text-white"
                />
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">Categories</label>
                <div className="grid grid-cols-2 gap-2">
                  {providerConfig.categories.map((category) => {
                    const selected = agentForm.categories.includes(category);
                    return (
                      <button
                        key={category}
                        type="button"
                        onClick={() => setAgentForm((prev) => ({
                          ...prev,
                          categories: selected
                            ? prev.categories.filter((item) => item !== category)
                            : prev.categories.length >= 4
                              ? prev.categories
                              : [...prev.categories, category]
                        }))}
                        className={`rounded-lg border px-3 py-2 text-left text-sm ${selected ? 'border-emerald-500 bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300' : 'border-slate-300 bg-white text-slate-700 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-300'}`}
                      >
                        {category}
                      </button>
                    );
                  })}
                </div>
                {agentErrors.categories && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{agentErrors.categories}</p>}
              </div>

              <div className="rounded-xl border border-slate-200 p-4 dark:border-slate-700">
                <h4 className="mb-3 text-sm font-bold uppercase tracking-wide text-slate-900 dark:text-white">Permissions</h4>
                <div className="grid gap-3 md:grid-cols-2">
                  <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
                    <input
                      type="checkbox"
                      checked={agentForm.permissions.canSendOffers}
                      onChange={(e) => setAgentNestedField('permissions', 'canSendOffers', e.target.checked)}
                    />
                    Can send offers
                  </label>
                  <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
                    <input
                      type="checkbox"
                      checked={agentForm.permissions.canAutoSendOffers}
                      onChange={(e) => {
                        setAgentNestedField('permissions', 'canAutoSendOffers', e.target.checked);
                        setAgentForm((prev) => ({ ...prev, autoEngage: e.target.checked }));
                      }}
                    />
                    Can auto-engage
                  </label>
                  <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
                    <input
                      type="checkbox"
                      checked={agentForm.permissions.canUseDiscounts}
                      onChange={(e) => {
                        setAgentNestedField('permissions', 'canUseDiscounts', e.target.checked);
                        setAgentNestedField('pricingPolicy', 'allowDiscounts', e.target.checked);
                      }}
                    />
                    Can use discounts
                  </label>
                  <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
                    <input
                      type="checkbox"
                      checked={agentForm.permissions.requiresManualApproval}
                      onChange={(e) => setAgentNestedField('permissions', 'requiresManualApproval', e.target.checked)}
                    />
                    Require manual approval
                  </label>
                </div>
              </div>

              <div className="rounded-xl border border-slate-200 p-4 dark:border-slate-700">
                <h4 className="mb-3 text-sm font-bold uppercase tracking-wide text-slate-900 dark:text-white">Limits</h4>
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">Offers per day</label>
                    <input
                      type="number"
                      value={agentForm.limits.offersPerDay}
                      onChange={(e) => setAgentNestedField('limits', 'offersPerDay', Number(e.target.value))}
                      className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900 dark:border-slate-600 dark:bg-slate-900 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">Offers per week</label>
                    <input
                      type="number"
                      value={agentForm.limits.offersPerWeek}
                      onChange={(e) => setAgentNestedField('limits', 'offersPerWeek', Number(e.target.value))}
                      className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900 dark:border-slate-600 dark:bg-slate-900 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">Credits per day</label>
                    <input
                      type="number"
                      value={agentForm.limits.creditsPerDay}
                      onChange={(e) => setAgentNestedField('limits', 'creditsPerDay', Number(e.target.value))}
                      className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900 dark:border-slate-600 dark:bg-slate-900 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">Credits per week</label>
                    <input
                      type="number"
                      value={agentForm.limits.creditsPerWeek}
                      onChange={(e) => setAgentNestedField('limits', 'creditsPerWeek', Number(e.target.value))}
                      className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900 dark:border-slate-600 dark:bg-slate-900 dark:text-white"
                    />
                  </div>
                </div>
              </div>

              <div className="rounded-xl border border-slate-200 p-4 dark:border-slate-700">
                <h4 className="mb-3 text-sm font-bold uppercase tracking-wide text-slate-900 dark:text-white">Pricing Policy</h4>
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">Base price</label>
                    <input
                      type="number"
                      value={agentForm.pricingPolicy.basePrice}
                      onChange={(e) => setAgentNestedField('pricingPolicy', 'basePrice', e.target.value)}
                      className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900 dark:border-slate-600 dark:bg-slate-900 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">Minimum price</label>
                    <input
                      type="number"
                      value={agentForm.pricingPolicy.minPrice}
                      onChange={(e) => setAgentNestedField('pricingPolicy', 'minPrice', e.target.value)}
                      className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900 dark:border-slate-600 dark:bg-slate-900 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">Max discount %</label>
                    <input
                      type="number"
                      value={agentForm.pricingPolicy.maxDiscountPercent}
                      onChange={(e) => setAgentNestedField('pricingPolicy', 'maxDiscountPercent', Number(e.target.value))}
                      className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900 dark:border-slate-600 dark:bg-slate-900 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">Max discount amount</label>
                    <input
                      type="number"
                      value={agentForm.pricingPolicy.maxDiscountAmount}
                      onChange={(e) => setAgentNestedField('pricingPolicy', 'maxDiscountAmount', Number(e.target.value))}
                      className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900 dark:border-slate-600 dark:bg-slate-900 dark:text-white"
                    />
                  </div>
                </div>
              </div>

              <div className="rounded-xl border border-slate-200 p-4 dark:border-slate-700">
                <h4 className="mb-3 text-sm font-bold uppercase tracking-wide text-slate-900 dark:text-white">Matching Rules</h4>
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">Minimum mission budget</label>
                    <input
                      type="number"
                      value={agentForm.matchingPolicy.minBudget}
                      onChange={(e) => setAgentNestedField('matchingPolicy', 'minBudget', e.target.value)}
                      className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900 dark:border-slate-600 dark:bg-slate-900 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">Maximum mission budget</label>
                    <input
                      type="number"
                      value={agentForm.matchingPolicy.maxBudget}
                      onChange={(e) => setAgentNestedField('matchingPolicy', 'maxBudget', e.target.value)}
                      className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900 dark:border-slate-600 dark:bg-slate-900 dark:text-white"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">Preferred locations</label>
                    <input
                      value={agentForm.matchingPolicy.preferredLocations}
                      onChange={(e) => setAgentNestedField('matchingPolicy', 'preferredLocations', e.target.value)}
                      placeholder="e.g. Johannesburg, Sandton, Pretoria"
                      className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900 dark:border-slate-600 dark:bg-slate-900 dark:text-white"
                    />
                  </div>
                  <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300 md:col-span-2">
                    <input
                      type="checkbox"
                      checked={agentForm.matchingPolicy.allowCrossCategory}
                      onChange={(e) => setAgentNestedField('matchingPolicy', 'allowCrossCategory', e.target.checked)}
                    />
                    Allow cross-category missions
                  </label>
                </div>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={() => setShowAgentModal(false)} className="rounded-lg px-4 py-2 text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-700">Cancel</button>
              <button onClick={saveAgent} disabled={savingAgent} className="rounded-lg bg-emerald-600 px-4 py-2 text-white hover:bg-emerald-700 disabled:opacity-50">{savingAgent ? 'Saving...' : editingAgent ? 'Save Agent' : 'Create Agent'}</button>
            </div>
          </div>
        </div>
      )}

      {showPlayModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setShowPlayModal(false)}
        >
          <div
            className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl dark:bg-slate-800"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="mb-4 text-2xl font-bold text-slate-900 dark:text-white">{editingPlay ? 'Edit Agent Play' : 'Create Agent Play'}</h3>
            <div className="space-y-4">
              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">Play Name</label>
                <input value={playForm.name} onChange={(e) => setPlayForm((prev) => ({ ...prev, name: e.target.value }))} className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900 dark:border-slate-600 dark:bg-slate-900 dark:text-white" />
                {playErrors.name && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{playErrors.name}</p>}
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">Category</label>
                <select value={playForm.category} onChange={(e) => setPlayForm((prev) => ({ ...prev, category: e.target.value }))} className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900 dark:border-slate-600 dark:bg-slate-900 dark:text-white">
                  <option value="">Select category</option>
                  {selectedAgent?.categories?.map((category) => <option key={category} value={category}>{category}</option>)}
                </select>
                {playErrors.category && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{playErrors.category}</p>}
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">Agent Message</label>
                <textarea value={playForm.valueProp} onChange={(e) => setPlayForm((prev) => ({ ...prev, valueProp: e.target.value }))} rows={4} className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900 dark:border-slate-600 dark:bg-slate-900 dark:text-white" />
                {playErrors.valueProp && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{playErrors.valueProp}</p>}
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={() => setShowPlayModal(false)} className="rounded-lg px-4 py-2 text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-700">Cancel</button>
              <button onClick={savePlay} disabled={savingPlay} className="rounded-lg bg-purple-600 px-4 py-2 text-white hover:bg-purple-700 disabled:opacity-50">{savingPlay ? 'Saving...' : editingPlay ? 'Save Play' : 'Create Play'}</button>
            </div>
          </div>
        </div>
      )}

      {showOfferModal && selectedRequest && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setShowOfferModal(false)}
        >
          <div
            className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl dark:bg-slate-800"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-4 flex items-start justify-between gap-4">
              <div>
                <h3 className="text-2xl font-bold text-slate-900 dark:text-white">Send Agent Response</h3>
                <p className="text-sm text-slate-500 dark:text-slate-400">{selectedAgent?.name} responding to {selectedRequest.title || selectedRequest.description}</p>
              </div>
              {selectedPlay && <div className="rounded-lg bg-purple-50 px-3 py-2 text-xs font-medium text-purple-700 dark:bg-purple-900/20 dark:text-purple-300">Using play: {selectedPlay.name}</div>}
            </div>
            <div className="space-y-4">
              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">Message</label>
                <textarea value={offerForm.valueProp} onChange={(e) => setOfferForm((prev) => ({ ...prev, valueProp: e.target.value }))} rows={4} className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900 dark:border-slate-600 dark:bg-slate-900 dark:text-white" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">Price</label>
                  <input value={offerForm.price} onChange={(e) => setOfferForm((prev) => ({ ...prev, price: e.target.value }))} className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900 dark:border-slate-600 dark:bg-slate-900 dark:text-white" />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">Timeframe</label>
                  <input value={offerForm.timeframe} onChange={(e) => setOfferForm((prev) => ({ ...prev, timeframe: e.target.value }))} className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900 dark:border-slate-600 dark:bg-slate-900 dark:text-white" />
                </div>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={() => setShowOfferModal(false)} className="rounded-lg px-4 py-2 text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-700">Cancel</button>
              <button onClick={sendOffer} disabled={sendingOffer} className="rounded-lg bg-emerald-600 px-4 py-2 text-white hover:bg-emerald-700 disabled:opacity-50">{sendingOffer ? 'Sending...' : `Send Response (${creditsConfig.costPerOffer} credits)`}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProviderView;
